import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { supabase, sessionManager } from '../services/supabase';
import { User } from '../types';
import { tokenService } from '../services/tokenService';
import { sessionTimeoutService } from '../services/sessionTimeoutService';
import { tokenBlacklistService } from '../services/tokenBlacklistService';
import { AuthError, PostgrestError } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | PostgrestError | null }>;
  signOut: () => Promise<void>;
  isAdmin: () => boolean;
  isHR: () => boolean;
  isManager: () => boolean;
  isEmployee: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);
  const authStateRef = useRef<string>('initial');
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoized function to fetch user data
  const fetchUserData = useCallback(async (userId: string): Promise<User | null> => {
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('AuthContext: Error fetching user data:', error);
        return null;
      }
      
      return userData as User;
    } catch (error) {
      console.error('AuthContext: Error in fetchUserData:', error);
      return null;
    }
  }, []);

  // Optimized session initialization
  const initializeSession = useCallback(async () => {
    if (sessionChecked) return;
    
    try {
      console.log('AuthContext: Initializing session...');
      authStateRef.current = 'checking';
      setLoading(true);
      
      // Check if current token is blacklisted
      const session = await sessionManager.getValidSession();
      if (session?.access_token) {
        const isBlacklisted = await tokenBlacklistService.isTokenBlacklisted(session.access_token);
        if (isBlacklisted) {
          console.log('AuthContext: Token is blacklisted, clearing session');
          await sessionManager.clearSession();
          setUser(null);
          authStateRef.current = 'unauthenticated';
          return;
        }
      }
      
      if (session?.user) {
        console.log('AuthContext: Valid session found');
        // Validate token and refresh if needed
        const { isValid } = await tokenService.validateToken();
        if (isValid) {
          const userData = await fetchUserData(session.user.id);
          setUser(userData);
          authStateRef.current = 'authenticated';
          // Start session timeout monitoring
          sessionTimeoutService.start();
        } else {
          console.log('AuthContext: Token validation failed, clearing session');
          await sessionManager.clearSession();
          setUser(null);
          authStateRef.current = 'unauthenticated';
        }
      } else {
        console.log('AuthContext: No valid session');
        setUser(null);
        authStateRef.current = 'unauthenticated';
      }
    } catch (error) {
      console.error('AuthContext: Session initialization error:', error);
      await sessionManager.clearSession();
      setUser(null);
      authStateRef.current = 'error';
    } finally {
      setSessionChecked(true);
      setInitialLoad(false);
      setLoading(false);
    }
  }, [sessionChecked, fetchUserData]);

  useEffect(() => {
    initializeSession();

    // Optimized auth state change listener
    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('AuthContext: Auth state change event:', event, 'Current state:', authStateRef.current);
        
        // Prevent infinite loading by managing state transitions carefully
        const shouldShowLoading = (
          event === 'SIGNED_IN' || 
          event === 'SIGNED_OUT' || 
          (event === 'TOKEN_REFRESHED' && initialLoad)
        );
        
        // Clear any existing loading timeout
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        
        // Handle different auth events
        switch (event) {
          case 'SIGNED_IN':
            if (authStateRef.current !== 'authenticated') {
              setLoading(shouldShowLoading);
              if (session?.user) {
                // Validate token and start monitoring
                const { isValid } = await tokenService.validateToken();
                if (isValid) {
                  const userData = await fetchUserData(session.user.id);
                  setUser(userData);
                  authStateRef.current = 'authenticated';
                  sessionTimeoutService.start();
                } else {
                  console.log('AuthContext: Token validation failed on sign in');
                  await sessionManager.clearSession();
                }
              }
              setLoading(false);
            }
            break;
            
          case 'SIGNED_OUT':
            if (authStateRef.current !== 'unauthenticated') {
              setLoading(shouldShowLoading);
              // Stop session monitoring and clear all security services
              sessionTimeoutService.stop();
              tokenService.clearCache();
              await tokenBlacklistService.cleanupExpiredEntries();
              setUser(null);
              authStateRef.current = 'unauthenticated';
              // Clear any cached user data
              localStorage.removeItem('payroll-user-data');
              setLoading(false);
            }
            break;
            
          case 'TOKEN_REFRESHED':
            console.log('AuthContext: Token refreshed');
            // Validate the new token
            const { isValid: refreshValid } = await tokenService.validateToken();
            if (!refreshValid) {
              console.log('AuthContext: Refreshed token validation failed');
              await sessionManager.clearSession();
            }
            break;
            
          case 'PASSWORD_RECOVERY':
          case 'USER_UPDATED':
            // Handle these events without changing loading state
            if (session?.user && authStateRef.current === 'authenticated') {
              const userData = await fetchUserData(session.user.id);
              if (userData) {
                setUser(userData);
              }
            }
            break;
            
          default:
            console.log('AuthContext: Unhandled auth event:', event);
        }
        
        // Safety timeout to prevent infinite loading
        if (shouldShowLoading) {
          loadingTimeoutRef.current = setTimeout(() => {
            console.warn('AuthContext: Loading timeout reached, forcing loading to false');
            setLoading(false);
          }, 10000); // 10 second timeout
        }
      }
    );

    return () => {
      listener?.subscription.unsubscribe();
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [initialLoad, fetchUserData, initializeSession]);

  // Enhanced sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      authStateRef.current = 'signing_in';
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        authStateRef.current = 'unauthenticated';
        setLoading(false);
        return { error };
      }
      
      // The auth state change listener will handle setting the user data
      // No need to manually fetch and set user data here
      return { error: null };
    } catch (error) {
      authStateRef.current = 'error';
      setLoading(false);
      return { error: error as AuthError };
    }
  };

  // Enhanced sign out with proper cleanup
  const signOut = async () => {
    try {
      setLoading(true);
      authStateRef.current = 'signing_out';
      
      // Immediate UI feedback - set user to null right away
      setUser(null);
      
      // Perform all cleanup operations in parallel with short timeouts
      const cleanupOperations = [];
      
      // Blacklist current token (non-blocking with short timeout)
      try {
        const session = await sessionManager.getValidSession();
        if (session?.access_token) {
          const blacklistPromise = tokenBlacklistService.blacklistToken(
            session.access_token,
            session.user.id
          ).catch(error => console.warn('Token blacklisting failed:', error));
          
          cleanupOperations.push(
            Promise.race([
              blacklistPromise,
              new Promise(resolve => setTimeout(resolve, 1000)) // 1 second timeout
            ])
          );
        }
      } catch (sessionError) {
        console.warn('Failed to get session for blacklisting:', sessionError);
      }
      
      // Stop session monitoring immediately
      sessionTimeoutService.stop();
      
      // Clear all security service caches immediately
      tokenService.clearCache();
      
      // Add cleanup operations (non-blocking)
      cleanupOperations.push(
        tokenBlacklistService.cleanupExpiredEntries().catch(error => 
          console.warn('Cleanup failed:', error)
        )
      );
      
      // Add session clearing (local only)
      cleanupOperations.push(
        sessionManager.clearSession().catch(error => 
          console.warn('Session clearing failed:', error)
        )
      );
      
      // Wait for all operations with a maximum timeout of 2 seconds
      try {
        await Promise.race([
          Promise.allSettled(cleanupOperations),
          new Promise(resolve => setTimeout(resolve, 2000))
        ]);
      } catch (error) {
        console.warn('Some cleanup operations failed:', error);
      }
      
      // Ensure final state is set
      authStateRef.current = 'unauthenticated';
      setLoading(false);
      
    } catch (error) {
      console.error('Error during sign out:', error);
      // Force immediate cleanup
      setUser(null);
      authStateRef.current = 'unauthenticated';
      setLoading(false);
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.role === 'admin';
  };
  
  // Check if user is HR
  const isHR = () => {
    return user?.role === 'hr';
  };
  
  // Check if user is manager
  const isManager = () => {
    return user?.role === 'manager';
  };
  
  // Check if user is employee
  const isEmployee = () => {
    return user?.role === 'employee';
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
    isAdmin,
    isHR,
    isManager,
    isEmployee,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};